"use client"

import { Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  ArrowDownRight,
  ArrowUpRight,
  Copy,
  CreditCard,
  Building,
  CheckCircle,
  Wallet,
  User,
  Calendar,
  AlertCircle,
  Download,
  Shield,
  Clock,
  FileText,
  Link as LinkIcon,
} from "lucide-react"
import toast from "react-hot-toast"
import { useCurrentUser } from "../../utils/context/UserProvider"

const TransactionDetailModal = ({ isOpen, onClose, transaction }) => {
  const [copySuccess, setCopySuccess] = useState(null)
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [currentImage, setCurrentImage] = useState(null)
  const { currentUser } = useCurrentUser()
  if (!transaction) return null

  const rawData = transaction.rawData || transaction

  // Pretty date formatter
  const formatDate = (raw) => {
    if (!raw) return "N/A";
    const dateString = String(raw).trim();
    const locale = navigator?.language || "en-US";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;
    const hasExplicitTime =
      /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString);
    let d;
    const m = dateString.match(dotDateRE);
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m;
      d = new Date(
        parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0, MM ? parseInt(MM, 10) : 0, 0, 0
      );
    } else {
      d = new Date(dateString);
    }
    if (isNaN(d.getTime())) return raw;
    const datePart = new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric", timeZone }).format(d);
    if (!hasExplicitTime) return datePart;
    const timePart = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(d);
    return `${datePart} · ${timePart}`;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "pending":   return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
      case "rejected":  return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      default:          return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
    }
  }

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
    setCopySuccess(type)
    toast.success(`${type} copied to clipboard`)
    setTimeout(() => setCopySuccess(null), 2000)
  }

  const getPaymentMethodIcon = (method) => {
    if (!method) return <CreditCard size={14} />
    const m = method.toLowerCase()
    if (m.includes("card") || m.includes("credit")) return <CreditCard size={14} />
    if (m.includes("bank") || m.includes("transfer")) return <Building size={14} />
    if (m.includes("wallet") || m.includes("usdt") || m.includes("crypto")) return <Wallet size={14} />
    return <CreditCard size={14} />
  }

  const accountDetails = rawData.credit_account && typeof rawData.credit_account === "object" ? rawData.credit_account : null
  const userDetails = rawData.user && typeof rawData.user === "object" ? rawData.user : null

  const openImageModal = (imageUrl) => {
    setCurrentImage(imageUrl)
    setImageModalOpen(true)
  }

  const isPDF = (url = "") => /\.pdf(\?|$)/i.test(url)
  const fileNameFromUrl = (url = "", fallback = "receipt") => {
    try {
      const u = new URL(url, window.location.href)
      const last = u.pathname.split("/").filter(Boolean).pop() || fallback
      return last
    } catch {
      const parts = url.split("?")[0].split("/")
      return parts.pop() || fallback
    }
  }

  const downloadFile = (fileUrl, suggested = "receipt") => {
    if (!fileUrl) return
    const name = fileNameFromUrl(fileUrl, suggested)
    const a = document.createElement("a")
    a.href = fileUrl
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
    toast.success("File download started")
  }

  const methodLower =
    transaction.paymentMethod?.toLowerCase() ||
    rawData.payment_method?.toLowerCase() ||
    ""

  const paymentDetails = transaction.details || rawData.payment_details || {}
  const cryptoCoin    = paymentDetails.coin
  const cryptoNetwork = paymentDetails.network
  const cryptoTx      = paymentDetails.tx_id || paymentDetails.txId || paymentDetails.transactionId
  const cryptoFrom    = paymentDetails.address_from || paymentDetails.wallet_address || paymentDetails.address
  const cryptoTo      = paymentDetails.address_to
  const cryptoProof   = paymentDetails.confirmation_image_url || rawData.confirmation_image
  const bankReceipt   = paymentDetails.receipt_url || rawData.confirmation_image

  const paymentId = rawData.transaction_id || rawData._id || transaction.id || null

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>

          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          {/* Side Panel */}
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-200"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col h-full bg-white dark:bg-neutral-950 border-l border-neutral-200 dark:border-neutral-800 shadow-2xl">

                    {/* ── Header ── */}
                    <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                          {transaction.type === "deposit" ? (
                            <ArrowDownRight className="h-5 w-5 text-green-500 dark:text-green-400" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5 text-red-500 dark:text-red-400" />
                          )}
                          {transaction.type === "deposit" ? "Deposit" : "Refund"} Details
                        </Dialog.Title>
                        <button
                          onClick={onClose}
                          className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                          type="button"
                          aria-label="Close"
                        >
                          <X size={20} />
                        </button>
                      </div>

                      {/* Payment ID chip — clickable, shown below title */}
                      {paymentId && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(paymentId, "Payment ID")}
                          className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-800/70 transition-colors group"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="flex-shrink-0 text-[10px] font-semibold tracking-widest text-neutral-400 dark:text-neutral-500 uppercase">
                              ID
                            </span>
                            <span className="text-xs font-mono text-neutral-600 dark:text-neutral-300 truncate">
                              {paymentId}
                            </span>
                          </div>
                          {copySuccess === "Payment ID"
                            ? <CheckCircle size={13} className="flex-shrink-0 text-green-500" />
                            : <Copy size={13} className="flex-shrink-0 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-200 transition-colors" />
                          }
                        </button>
                      )}
                    </div>

                    {/* ── Scrollable Content ── */}
                    <div className="flex-1 overflow-y-auto">
                      <div className="p-6">

                        {/* Amount */}
                        <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:bg-neutral-800 mb-6">
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">Amount</div>
                            <div className={`text-lg font-semibold ${transaction.type === "deposit" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                              {transaction.type === "deposit" ? "+" : "-"}${Number(transaction.amount || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {/* Main rows */}
                        <div className="space-y-4 mb-6">
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">Date</div>
                            <div className="text-sm text-neutral-900 dark:text-white flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(rawData.createdAt) || transaction.date}
                            </div>
                          </div>

                          <div className="flex justify-between items-center">
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">Status</div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(transaction.status)}`}>
                              <span className={`w-2 h-2 mr-1.5 rounded-full ${
                                transaction.status === "completed" ? "bg-green-500"
                                : transaction.status === "pending" ? "bg-yellow-500"
                                : "bg-red-500"
                              }`} />
                              {transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1)}
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">Payment Method</div>
                            <div className="text-sm text-neutral-900 dark:text-white flex items-center gap-2">
                              {getPaymentMethodIcon(methodLower)}
                              {transaction.paymentMethod || rawData.payment_method}
                              {methodLower.includes("crypto") && cryptoCoin ? ` · ${cryptoCoin}` : null}
                            </div>
                          </div>

                          {userDetails && (
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">Client Reference</div>
                              <div className="text-sm text-neutral-900 dark:text-white flex items-center gap-2">
                                <User size={14} />
                                {userDetails.reference || userDetails.fullName || "Client"}
                              </div>
                            </div>
                          )}

                          {rawData.processingTime && (
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">Processing Time</div>
                              <div className="text-sm text-neutral-900 dark:text-white">{rawData.processingTime}</div>
                            </div>
                          )}

                          {rawData.payment_details?.platform_name && (
                            <div className="flex justify-between items-center">
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">Platform</div>
                              <div className="text-sm text-neutral-900 dark:text-white max-w-[200px] truncate">
                                {rawData.payment_details.platform_name}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Status Change History */}
                        {(transaction.statusChangedBy || rawData.processed_by) && (
                          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl border border-purple-100 dark:border-purple-800/30 mb-6">
                            <h3 className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-3 flex items-center gap-2">
                              <Shield size={16} />
                              Status Change History
                            </h3>
                            <div className="space-y-3">
                              {currentUser?.role?.name !== "Client" && (
                                <div className="flex justify-between items-start">
                                  <div className="text-sm text-purple-600 dark:text-purple-300">Changed by</div>
                                  <div className="text-sm text-purple-800 dark:text-purple-200 text-right">
                                    <div className="font-medium">
                                      {transaction.statusChangedBy?.name || rawData.processed_by?.fullName || "Admin"}
                                    </div>
                                    {transaction.statusChangedBy?.role !== "SuperAdmin" || rawData.processed_by?.role?.name !== "SuperAdmin" ? (
                                      <div className="text-xs text-purple-500 dark:text-purple-400">
                                        {transaction.statusChangedBy?.role || rawData.processed_by?.role?.name || "Admin"}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              )}

                              {(transaction.statusChangedAt || rawData.status_changed_at) && (
                                <div className="flex justify-between items-center">
                                  <div className="text-sm text-purple-600 dark:text-purple-300">Changed at</div>
                                  <div className="text-sm text-purple-800 dark:text-purple-200 flex items-center gap-1">
                                    <Clock size={12} />
                                    {formatDate(transaction.statusChangedAt || rawData.status_changed_at)}
                                  </div>
                                </div>
                              )}

                              {(transaction.previousStatus || rawData.previous_status) && (
                                <div className="flex justify-between items-center">
                                  <div className="text-sm text-purple-600 dark:text-purple-300">Previous status</div>
                                  <div className="text-sm text-purple-800 dark:text-purple-200">
                                    {((transaction.previousStatus || rawData.previous_status).charAt(0).toUpperCase() +
                                      (transaction.previousStatus || rawData.previous_status).slice(1))}
                                  </div>
                                </div>
                              )}

                              {(transaction.rejectionReason || rawData.rejection_reason) && (
                                <div className="flex flex-col gap-1">
                                  <div className="text-sm text-purple-600 dark:text-purple-300">Reason</div>
                                  <div className="text-sm text-purple-800 dark:text-purple-200 bg-purple-100 dark:bg-purple-800/20 p-2 rounded">
                                    {transaction.rejectionReason || rawData.rejection_reason}
                                  </div>
                                </div>
                              )}

                              {(transaction.adminNotes || rawData.admin_notes) && (
                                <div className="flex flex-col gap-1">
                                  <div className="text-sm text-purple-600 dark:text-purple-300">Admin Notes</div>
                                  <div className="text-sm text-purple-800 dark:text-purple-200 bg-purple-100 dark:bg-purple-800/20 p-2 rounded">
                                    {transaction.adminNotes || rawData.admin_notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Payment Details */}
                        {(transaction.details || rawData.payment_details) && (
                          <div className="bg-neutral-50 w-full dark:border-neutral-900 dark:bg-neutral-900/30 p-4 rounded-xl border border-neutral-200 dark:bg-neutral-800 mb-6">
                            <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-3">Payment Details</h3>
                            <div className="space-y-2">
                              {(() => {
                                if (methodLower.includes("paypal")) {
                                  return (
                                    <>
                                      {paymentDetails.email && <Row label="PayPal Email" value={paymentDetails.email} />}
                                      {(paymentDetails.transaction_id || paymentDetails.transactionId) && (
                                        <Row label="Transaction ID" value={paymentDetails.transaction_id || paymentDetails.transactionId} />
                                      )}
                                      {paymentDetails.status && <Row label="Status" value={paymentDetails.status} />}
                                    </>
                                  )
                                } else if (methodLower.includes("card") || methodLower.includes("credit")) {
                                  return (
                                    <>
                                      {(paymentDetails.last4 || paymentDetails.cardLast4) && (
                                        <Row label="Card Number" value={`**** **** **** ${paymentDetails.last4 || paymentDetails.cardLast4}`} />
                                      )}
                                      {paymentDetails.brand && <Row label="Card Brand" value={String(paymentDetails.brand).toUpperCase()} />}
                                      {(paymentDetails.payment_intent_id || paymentDetails.paymentIntentId) && (
                                        <Row label="Payment Intent" value={paymentDetails.payment_intent_id || paymentDetails.paymentIntentId} />
                                      )}
                                    </>
                                  )
                                } else if (methodLower.includes("bank") || methodLower.includes("transfer")) {
                                  return (
                                    <>
                                      {(paymentDetails.bank_name || paymentDetails.bankName) && (
                                        <Row label="Bank Name" value={paymentDetails.bank_name || paymentDetails.bankName} />
                                      )}
                                      {userDetails && (
                                        <div className="flex justify-between items-center">
                                          <div className="text-sm text-neutral-600 dark:text-neutral-400">Client ID</div>
                                          <div className="text-sm text-neutral-900 dark:text-white flex items-center gap-2">
                                            {userDetails.clientId}
                                          </div>
                                        </div>
                                      )}
                                      {paymentDetails.platform_name && <Row label="Platform" value={paymentDetails.platform_name} />}
                                      {bankReceipt && (
                                        <ReceiptRow
                                          label="Receipt"
                                          fileUrl={bankReceipt}
                                          onPreview={(url) => (isPDF(url) ? window.open(url, "_blank") : openImageModal(url))}
                                          onDownload={(url) => downloadFile(url, "bank-receipt")}
                                        />
                                      )}
                                    </>
                                  )
                                } else if (methodLower.includes("usdt") || methodLower.includes("crypto")) {
                                  return (
                                    <>
                                      {cryptoCoin    && <Row label="Token"          value={cryptoCoin} />}
                                      {cryptoNetwork && <Row label="Network"        value={cryptoNetwork} />}
                                      {cryptoFrom    && <Row label="Address From"   value={cryptoFrom} />}
                                      {cryptoTo      && <Row label="Address To"     value={cryptoTo} />}
                                      {cryptoTx && (
                                        <div className="flex justify-between items-start gap-3">
                                          <div className="text-sm text-neutral-600 dark:text-neutral-400 shrink-0">Transaction Link</div>
                                          <a
                                            href={cryptoTx.startsWith("http") ? cryptoTx : `https://${cryptoTx}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-2 transition-colors max-w-[200px] truncate"
                                          >
                                            <LinkIcon size={13} className="flex-shrink-0" />
                                            <span className="truncate">{cryptoTx}</span>
                                          </a>
                                        </div>
                                      )}
                                      {cryptoProof && (
                                        <ReceiptRow
                                          label="Confirmation"
                                          fileUrl={cryptoProof}
                                          onPreview={(url) => (isPDF(url) ? window.open(url, "_blank") : openImageModal(url))}
                                          onDownload={(url) => downloadFile(url, "crypto-proof")}
                                        />
                                      )}
                                    </>
                                  )
                                } else {
                                  return Object.entries(paymentDetails).map(([key, value]) => {
                                    if (!value || typeof value === "object") return null
                                    const formattedKey = key
                                      .replace(/_/g, " ")
                                      .replace(/([A-Z])/g, " $1")
                                      .replace(/^./, (str) => str.toUpperCase())
                                    return <Row key={key} label={formattedKey} value={String(value)} />
                                  })
                                }
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {rawData.notes && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 mb-6">
                            <h3 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                              <FileText size={16} />
                              Notes
                            </h3>
                            <p className="text-sm text-blue-600 dark:text-blue-300">{rawData.notes}</p>
                          </div>
                        )}

                        {/* Error */}
                        {rawData.error_message && (
                          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-100 dark:border-red-800/30 mb-6 flex items-start gap-2">
                            <AlertCircle size={16} className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <h3 className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</h3>
                              <p className="text-sm text-red-600 dark:text-red-300">{rawData.error_message}</p>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>

                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Image Preview Modal */}
      <Transition appear show={imageModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setImageModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative max-w-3xl w-full transform overflow-hidden rounded-xl bg-transparent transition-all">
                  <button
                    onClick={() => setImageModalOpen(false)}
                    className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
                    type="button"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>
                  <div className="relative">
                    <img
                      src={currentImage || "/placeholder.svg"}
                      alt="Receipt"
                      className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
                    />
                    <div className="absolute bottom-4 right-4 flex gap-2">
                      <button
                        onClick={() => window.open(currentImage, "_blank")}
                        className="p-3 rounded-full bg-white/90 dark:bg-black/70 text-neutral-700 dark:text-white shadow-lg hover:bg-white dark:hover:bg-black/90 transition-colors"
                        title="Open in new tab"
                      >
                        <LinkIcon size={20} />
                      </button>
                      <button
                        onClick={() => downloadFile(currentImage, "receipt")}
                        className="p-3 rounded-full bg-white/90 dark:bg-black/70 text-neutral-700 dark:text-white shadow-lg hover:bg-white dark:hover:bg-black/90 transition-colors"
                        title="Download receipt"
                      >
                        <Download size={20} />
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

/** Sub-components */
const Row = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <div className="text-sm text-neutral-600 dark:text-neutral-400">{label}</div>
    <div className="text-sm text-neutral-900 dark:text-white max-w-[200px] truncate">{value}</div>
  </div>
)

const ReceiptRow = ({ label, fileUrl, onPreview, onDownload }) => {
  const _isPDF = /\.pdf(\?|$)/i.test(fileUrl || "")
  return (
    <div className="flex justify-between items-center">
      <div className="text-sm text-neutral-600 dark:text-neutral-400">{label}</div>
      <button
        onClick={() => onPreview(fileUrl)}
        className="inline-flex items-center dark:text-neutral-200 gap-2 px-3 py-1.5 rounded-xl bg-neutral-100 dark:bg-neutral-950 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors text-sm"
      >
        <LinkIcon size={16} />
        {_isPDF ? "Open PDF" : "Preview Image"}
      </button>
    </div>
  )
}

export default TransactionDetailModal
